/**
 * 用户管理（租户管理员 / 技术实施组）
 *
 * 与普通后台的差别在于两点，都来自业务约束：
 *   1. 用户必须绑定**角色**，角色决定数据权限与金额/风险上限，因此新建用户时强制选角色；
 *   2. 手机号等敏感字段按权限脱敏展示，避免管理员页面成为信息泄露的出口。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined, StopOutlined, UserAddOutlined } from '@ant-design/icons';
import {
  DATA_SCOPE_LABELS,
  PermissionCode,
  USER_STATUS_LABELS,
  UserStatus,
  type Dept,
  type Role,
  type User,
} from '@ilc/shared';
import { orgApi } from '@/api/endpoints';
import { mockDeptList, mockRoleList, mockUserList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { MaskedField } from '@/components/MaskedField';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  deptId?: string;
  status?: UserStatus;
  roleId?: string;
}

const USERNAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{3,63}$/;
const PHONE_PATTERN = /^1[3-9]\d{9}$/;

const STATUS_COLORS: Record<UserStatus, string> = {
  ACTIVE: 'green',
  INVITED: 'blue',
  DISABLED: 'default',
  LOCKED: 'red',
};

export default function Users(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.USER_WRITE);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disableTarget, setDisableTarget] = useState<User | null>(null);

  const list = usePagedList<User, Query>({
    loader: (q) => orgApi.users(q),
    fallback: (q) => mockUserList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const roles = useResource<Role[]>(() => orgApi.roles(), { fallback: () => mockRoleList() });
  const depts = useResource<Dept[]>(() => orgApi.depts(), { fallback: () => mockDeptList() });

  const roleOptions = (roles.data ?? []).map((r) => ({
    value: r.id,
    label: `${r.name}（${r.code}）`,
  }));

  const openCreate = (): void => {
    setCreating(true);
  };

  const openEdit = (user: User): void => {
    setEditing(user);
  };

  /**
   * 表单初值。**不要改回在 openEdit 里调 setFieldsValue**。
   *
   * 弹窗带 `destroyOnHidden`，关闭时会卸载内部的 `<Form>`；此刻 form 实例处于
   * "未连接"状态，同步调 `setFieldsValue` 会被直接丢弃，等 Form 重新挂载时
   * 又按 initialValues（空）初始化 —— 表现为"点编辑，标题对、里面全空"。
   * 本项目在 `LegalWorkbench.tsx` / `Roles.tsx` / `InvoiceAttachments.tsx`
   * 都有同一处修正与更详细的说明。
   */
  const formInitialValues = useMemo(
    () =>
      editing
        ? {
            username: editing.username,
            name: editing.name,
            deptId: editing.deptId ?? undefined,
            roleIds: (editing.roles ?? []).map((r) => r.id),
            phone: editing.phone ?? '',
            email: editing.email ?? '',
            dingtalkUserId: editing.dingtalkUserId ?? '',
          }
        : {},
    [editing],
  );

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    const deptId = values.deptId ? String(values.deptId) : undefined;
    // 创建接口的可选字段是 string | undefined，更新接口允许显式置空（null），
    // 因此两者分开组装，避免把 null 发给创建接口。
    const nullable = {
      name: String(values.name ?? ''),
      phone: values.phone ? String(values.phone) : null,
      email: values.email ? String(values.email) : null,
      dingtalkUserId: values.dingtalkUserId ? String(values.dingtalkUserId) : null,
      roleIds: (values.roleIds ?? []) as string[],
    };
    const result = await performAction({
      action: () =>
        editing
          ? orgApi.updateUser(editing.id, { ...nullable, deptId: deptId ?? null })
          : orgApi.createUser({
              username: String(values.username ?? ''),
              password: String(values.password ?? ''),
              name: nullable.name,
              roleIds: nullable.roleIds,
              phone: nullable.phone ?? undefined,
              email: nullable.email ?? undefined,
              dingtalkUserId: nullable.dingtalkUserId ?? undefined,
              ...(deptId ? { deptId } : {}),
            }),
      successMessage: editing ? '用户信息已更新' : '用户已创建，请将初始密码线下告知本人',
      offline: () => undefined,
      offlineMessage: '后端用户接口尚未就绪，本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setCreating(false);
      setEditing(null);
      form.resetFields();
      list.reload();
    }
  };

  const toggleStatus = async (user: User, next: UserStatus, reason: string): Promise<void> => {
    const result = await performAction({
      action: () => orgApi.updateUser(user.id, { status: next }),
      successMessage: next === UserStatus.DISABLED ? '用户已停用' : '用户已启用',
      offline: () =>
        list.setList((prev) => prev.map((item) => (item.id === user.id ? { ...item, status: next } : item))),
      offlineMessage: '后端用户接口尚未就绪，状态变更仅在本地演示',
    });
    if (result.ok && reason) {
      feedback.info(`操作理由已记录：${reason}`);
    }
    setDisableTarget(null);
    if (result.ok) list.reload();
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="用户管理"
        description="维护租户内用户、分配部门与角色。角色决定数据权限（本人/本部门/本租户）、可审核金额上限与可放行风险等级。"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={!canWrite} onClick={openCreate}>
              新增用户
            </Button>
          </Space>
        }
      />

      {!canWrite ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前账号为只读"
          description="您没有 user:write 权限，可以查看用户与角色分配情况，但不能新增、编辑或停用用户。"
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="角色即权限，请谨慎分配"
        description="给用户分配「租户管理员」等于交出全部权限（包含进项锁定这类不可逆操作）。建议按最小必要原则分配：经办人给「项目部经办人」，财务给对应岗位角色，需要初审权再叠加「财务初审岗」。"
      />

      <DataTable<User>
        storageKey="admin-users"
        exportFileName="用户列表"
        exportColumns={[
          { title: '账号', dataIndex: 'username' },
          { title: '姓名', dataIndex: 'name' },
          { title: '部门', dataIndex: 'deptName' },
          { title: '角色', value: (r) => (r.roles ?? []).map((x) => x.name).join('、') },
          { title: '状态', value: (r) => USER_STATUS_LABELS[r.status] },
          { title: '最后登录', dataIndex: 'lastLoginAt' },
        ]}
        columns={[
          {
            title: '账号',
            dataIndex: 'username',
            width: 150,
            fixed: 'left',
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          { title: '姓名', dataIndex: 'name', width: 110 },
          {
            title: '部门',
            dataIndex: 'deptName',
            width: 150,
            render: (v: string | null, record) => v ?? (record.deptId ? record.deptId : <Tag>未分配</Tag>),
          },
          {
            title: '角色',
            dataIndex: 'roles',
            width: 250,
            render: (v: User['roles']) =>
              v?.length ? (
                <Space wrap size={[4, 4]}>
                  {v.map((r) => (
                    <Tooltip key={r.id} title={r.code}>
                      <Tag color="blue">{r.name}</Tag>
                    </Tooltip>
                  ))}
                </Space>
              ) : (
                <Tag color="orange">未分配角色</Tag>
              ),
          },
          {
            title: '手机号',
            dataIndex: 'phone',
            width: 150,
            render: (v: string | null) => <MaskedField value={v} kind="phone" permission={PermissionCode.USER_WRITE} />,
          },
          {
            title: '钉钉 userId',
            dataIndex: 'dingtalkUserId',
            width: 150,
            render: (v: string | null) =>
              v ? (
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {v}
                </span>
              ) : (
                <Tooltip title="未绑定钉钉则无法接收待办推送">
                  <Tag>未绑定</Tag>
                </Tooltip>
              ),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (v: UserStatus) => <Tag color={STATUS_COLORS[v]}>{USER_STATUS_LABELS[v]}</Tag>,
          },
          {
            title: '最后登录',
            dataIndex: 'lastLoginAt',
            width: 150,
            render: (v: string | null) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v ? v.replace('T', ' ').slice(0, 16) : '从未登录'}
              </span>
            ),
          },
          {
            title: '操作',
            key: 'action',
            width: 170,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4}>
                <Button size="small" type="link" disabled={!canWrite} onClick={() => openEdit(record)}>
                  编辑
                </Button>
                {record.status === UserStatus.DISABLED ? (
                  <Button
                    size="small"
                    type="link"
                    disabled={!canWrite}
                    onClick={() => void toggleStatus(record, UserStatus.ACTIVE, '')}
                  >
                    启用
                  </Button>
                ) : (
                  <Button
                    size="small"
                    type="link"
                    danger
                    disabled={!canWrite}
                    onClick={() => setDisableTarget(record)}
                  >
                    停用
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="暂无用户"
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="账号 / 姓名 / 手机号"
              allowClear
              style={{ width: 220 }}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Select
              placeholder="部门"
              allowClear
              style={{ width: 160 }}
              value={list.query.deptId}
              onChange={(value) => list.setQuery({ deptId: value })}
              options={(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
            />
            <Select
              placeholder="角色"
              allowClear
              style={{ width: 200 }}
              value={list.query.roleId}
              onChange={(value) => list.setQuery({ roleId: value })}
              options={roleOptions}
            />
            <Select
              placeholder="状态"
              allowClear
              style={{ width: 120 }}
              value={list.query.status}
              onChange={(value) => list.setQuery({ status: value })}
              options={Object.entries(USER_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="角色概览与权限范围">
            {(roles.data ?? []).length === 0 ? (
              <Typography.Text type="secondary">角色接口未就绪或暂无角色数据</Typography.Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {(roles.data ?? []).map((role) => (
                  <Card key={role.id} size="small" styles={{ body: { padding: 10 } }}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space wrap>
                        <Typography.Text strong>{role.name}</Typography.Text>
                        <Tag>{role.code}</Tag>
                        {role.isSystem ? <Tag color="geekblue">系统预置</Tag> : null}
                        <Tag color="blue">数据权限：{DATA_SCOPE_LABELS[role.dataScope]}</Tag>
                        <Tag color="purple">
                          金额上限：
                          {role.amountLimitCents === null
                            ? '不限'
                            : `¥${(role.amountLimitCents / 100).toLocaleString('zh-CN')}`}
                        </Tag>
                        <Tag color="orange">最高风险：{role.maxRiskLevel ?? '不限'}</Tag>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {role.description ?? '无描述'} · 已分配 {role.userCount ?? 0} 人 · 权限{' '}
                        {role.permissions?.length ?? 0} 项
                      </Typography.Text>
                    </Space>
                  </Card>
                ))}
              </Space>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="组织与数据权限说明">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="数据权限如何生效">
                服务端按角色的数据权限自动改写查询范围（仅本人 / 本部门及以下 / 本租户 / 自定义），
                前端不做过滤——避免「前端藏起来、接口还能查到」的假安全。
              </Descriptions.Item>
              <Descriptions.Item label="金额与风险上限">
                财务初审时，超出金额上限或风险等级的发票，「通过」按钮会被禁用，需更高权限人员处理或走特例放行。
              </Descriptions.Item>
              <Descriptions.Item label="敏感字段">
                手机号、税号、银行账号按权限脱敏展示；有权限用户可按需查看明文，查看行为会记入操作日志。
              </Descriptions.Item>
              <Descriptions.Item label="停用与锁定">
                停用后用户无法登录但历史操作记录保留；锁定通常由连续登录失败触发，可由管理员解锁。
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Modal
        open={creating || editing !== null}
        title={editing ? `编辑用户 ${editing.username}` : '新增用户'}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
        width={620}
      >
        <Form
          /* key 取被编辑用户的 id：切换编辑对象或切到新增时整体重建，不留上一条的残留值 */
          key={editing?.id ?? 'create'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={formInitialValues}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="登录账号"
                rules={
                  editing
                    ? []
                    : [
                        { required: true, message: '请输入登录账号' },
                        {
                          pattern: USERNAME_PATTERN,
                          message: '以字母开头，4-64 位，仅可包含字母、数字与下划线',
                        },
                      ]
                }
                extra={editing ? '账号创建后不可修改' : '以字母开头，仅字母/数字/下划线'}
              >
                <Input className="ilc-mono" disabled={editing !== null} placeholder="如 zhangjingban" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                <Input placeholder="如 张经办" />
              </Form.Item>
            </Col>
            {!editing ? (
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="初始密码"
                  rules={[
                    { required: true, message: '请输入初始密码' },
                    { min: 8, message: '至少 8 位' },
                    { pattern: /(?=.*[A-Za-z])(?=.*\d)/, message: '需同时包含字母与数字' },
                  ]}
                  extra="请通过线下渠道告知本人，首次登录后建议修改"
                >
                  <Input.Password placeholder="至少 8 位，含字母与数字" />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={12}>
              <Form.Item name="deptId" label="所属部门">
                <Select
                  allowClear
                  placeholder="不选则不参与部门数据权限"
                  options={(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="手机号"
                rules={[{ pattern: PHONE_PATTERN, message: '请输入 11 位手机号' }]}
              >
                <Input className="ilc-mono" placeholder="用于登录与消息通知" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '邮箱格式不正确' }]}>
                <Input placeholder="选填" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dingtalkUserId"
                label="钉钉 userId"
                extra="绑定后才能接收待办推送（文档要求待办推送到钉钉）"
              >
                <Input className="ilc-mono" placeholder="选填" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="roleIds"
                label="角色"
                rules={[{ required: true, message: '至少分配一个角色' }]}
                extra="角色决定数据权限、可审核金额上限与可放行风险等级，请按最小必要原则分配"
              >
                <Select mode="multiple" placeholder="选择角色" options={roleOptions} optionFilterProp="label" showSearch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <ConfirmDanger
        open={disableTarget !== null}
        title={`停用用户 ${disableTarget?.name ?? ''}`}
        level="high"
        confirmText="确认停用"
        description={
          <Space direction="vertical" size={4}>
            <span>
              停用后该用户立即无法登录，但<strong>历史操作记录与已处理的发票不会被删除</strong>，
              仍可在操作日志中追溯。
            </span>
            <span>
              如果该用户是唯一持有「特例放行」或「进项锁定」权限的人，请先确认有人接手，否则高风险发票将无人可放行。
            </span>
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>
              该用户当前角色：{(disableTarget?.roles ?? []).map((r) => r.name).join('、') || '未分配'}
            </span>
          </Space>
        }
        reasonLabel="停用原因（必填，不少于 8 字，将写入操作日志）"
        reasonPresets={['该员工已离职，账号需停用', '岗位调整，权限需要重新分配', '账号疑似异常登录，先停用再排查']}
        onConfirm={(reason) => {
          if (disableTarget) return toggleStatus(disableTarget, UserStatus.DISABLED, reason);
          return undefined;
        }}
        onCancel={() => setDisableTarget(null)}
      />

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        <UserAddOutlined /> 新建用户后建议立刻在「角色」中核对权限：默认角色是最小可用集，
        需要初审、特例放行、进项锁定等高危权限时再单独叠加。
      </Typography.Paragraph>
    </div>
  );
}
